import { FC } from 'react';

import { Select, Tag } from 'antd';
import Search from 'antd/es/input/Search';
import { ColumnsType } from 'antd/es/table';
import { paymentWebhookOutcomeOptions } from 'common/constants';
import { IPaymentWebhookRequest } from 'common/types/payment-diagnostics';
import { parseDate } from 'common/utils';
import { Table } from 'components/Table';
import { observer } from 'mobx-react-lite';

import { formatDuration, webhookOutcomeColor } from '../helpers';
import {
  PAYMENT_LOGS_PAGE_SIZE,
  PaymentLogsViewModel,
} from '../payment-logs-view-model';

import styles from './webhook-requests-tab.module.scss';

interface IWebhookRequestsTabProps {
  model: PaymentLogsViewModel;
}

const getColumns = (
  onIntentClick: (intentId: string) => void,
): ColumnsType<IPaymentWebhookRequest> => [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
  {
    title: 'Outcome',
    dataIndex: 'outcome',
    key: 'outcome',
    width: 150,
    render: (outcome) => (
      <Tag color={webhookOutcomeColor(outcome)}>{outcome}</Tag>
    ),
  },
  {
    title: 'Order',
    dataIndex: 'orderIdSnapshot',
    key: 'orderIdSnapshot',
    width: 90,
    render: (orderId) => orderId ?? '—',
  },
  {
    title: 'Intent ID',
    dataIndex: 'providerIntentId',
    key: 'providerIntentId',
    width: 300,
    render: (intentId) =>
      intentId ? (
        <span className={styles.intentId} onClick={() => onIntentClick(intentId)}>
          {intentId}
        </span>
      ) : (
        '—'
      ),
  },
  {
    title: 'HTTP',
    dataIndex: 'httpStatus',
    key: 'httpStatus',
    width: 80,
    render: (status) => status ?? '—',
  },
  {
    title: 'Duration',
    dataIndex: 'durationMs',
    key: 'durationMs',
    width: 100,
    render: (duration) => formatDuration(duration),
  },
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    width: 170,
    render: (date) => parseDate(date),
  },
  {
    title: 'Completed',
    dataIndex: 'completedAt',
    key: 'completedAt',
    width: 170,
    render: (date) => (date ? parseDate(date) : '—'),
  },
  {
    title: 'Error',
    dataIndex: 'errorMessage',
    key: 'errorMessage',
    render: (message, record) =>
      message ? `${record.errorCode ?? ''} ${message}`.trim() : '—',
  },
];

export const WebhookRequestsTab: FC<IWebhookRequestsTabProps> = observer(
  ({ model }) => {
    const handleIntentClick = (intentId: string) =>
      model.openDiagnostics('intent', intentId);

    return (
      <div className={styles.container}>
        <div className={styles.filters}>
          <Select
            allowClear
            value={model.webhookOutcome}
            options={paymentWebhookOutcomeOptions}
            placeholder="Filter by outcome"
            disabled={model.webhookRequestsLoading}
            className={styles.field}
            onChange={model.changeWebhookOutcome}
          />
          <Search
            allowClear
            value={model.webhookIntentId}
            placeholder="Filter by intent ID"
            disabled={model.webhookRequestsLoading}
            className={styles.searchField}
            onChange={(e) => model.changeWebhookIntentId(e.target.value)}
            onSearch={model.searchWebhookRequests}
          />
        </div>
        <Table
          bordered
          rowKey="id"
          columns={getColumns(handleIntentClick)}
          data={model.webhookRequests}
          scroll={{ x: 1400 }}
          loading={model.webhookRequestsLoading}
          pagination={{
            total: model.webhookRequestsCount,
            current: model.webhookRequestsPage,
            pageSize: PAYMENT_LOGS_PAGE_SIZE,
            disabled: model.webhookRequestsLoading,
          }}
          onChange={(pagination) =>
            model.changeWebhookRequestsPage(pagination.current ?? 1)
          }
        />
      </div>
    );
  },
);
